/**
 * DnaCard — renders a client's derived DNA (values / exclusions / tilts / promises /
 * life events + temperament) with provenance. TASK-474.
 *
 * Self-fetches from GET /api/knowledge-graph/{clientId}/dna. DNA items are derived,
 * RM-reviewed facts — each shows a confidence dot and its source count.
 */

import React from 'react';
import { Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import {
  type DnaResponse,
  type DnaItem,
} from '../../services/knowledgeGraphService';
import { useClientDna } from '../../hooks/useClientDna';
import { formatLocator } from '../provenance/ProvenanceChain';
import { DnaRadar } from './DnaRadar';

// Display-shaping filters (TASK-495 contract, FILTER_WHITELIST.DnaCard): applied
// client-side over the already-fetched DNA — no fetch change.
export interface DnaCardFilters {
  sections?: readonly string[];
  minConfidence?: number;
  verifiedOnly?: boolean;
}

interface DnaCardProps {
  clientId: string;
  filters?: DnaCardFilters;
}

type SectionKey = 'values' | 'exclusions' | 'tilts' | 'promises' | 'life_events';

const SECTIONS: Array<{ key: SectionKey; label: string; chip: string }> = [
  { key: 'values', label: 'Values', chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'exclusions', label: 'Exclusions', chip: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'tilts', label: 'Tilts', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'promises', label: 'Promises', chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'life_events', label: 'Life Events', chip: 'bg-gray-50 text-gray-700 border-gray-200' },
];

function confidenceDot(confidence: number | null): { color: string; label: string } {
  const c = confidence ?? 0;
  if (c >= 0.85) return { color: 'bg-green-500', label: 'High confidence' };
  if (c >= 0.65) return { color: 'bg-amber-500', label: 'Medium confidence' };
  return { color: 'bg-gray-400', label: 'Low confidence' };
}

const DnaItemRow: React.FC<{ item: DnaItem }> = ({ item }) => {
  const dot = confidenceDot(item.confidence);
  const sourceCount = item.sources?.length ?? 0;
  // TASK-682's citation (review fix). `/dna` dropped these columns, so the one surface an
  // RM is most likely to challenge — "how do you know he said that?" — was the one that
  // could not answer. A claim with no stored excerpt renders no citation at all.
  const locator = formatLocator(item.source_locator);
  return (
    <li className="py-1" title={item.text}>
      <div className="flex items-start gap-2">
        <span
          className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dot.color}`}
          aria-label={dot.label}
        />
        <span className="text-sm text-gray-700 flex-1">
          {item.text}
          {item.tag && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">
              {item.tag}
            </span>
          )}
        </span>
        {/* TASK-698: two-sided on purpose. A green shield when verified and NOTHING when not is
            unreadable — the RM cannot tell "nobody has confirmed this" from "this component does
            not show verification". Every one of the 227 live rows was unverified while the card
            looked exactly as it does for a confirmed fact. Same slot, no new badge. */}
        {item.verified ? (
          <ShieldCheck className="w-3.5 h-3.5 text-green-600 mt-0.5" aria-label="Verified" />
        ) : (
          <span
            data-testid="dna-unverified"
            className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap"
            title="No one has confirmed this fact yet"
          >
            unconfirmed
          </span>
        )}
        {sourceCount > 0 && (
          <span className="text-xs text-gray-400 mt-0.5" title={`${sourceCount} source(s)`}>
            {sourceCount} src
          </span>
        )}
      </div>
      {item.source_excerpt && (
        <p
          data-testid="dna-citation"
          className="ml-4 mt-0.5 border-l-2 border-gray-200 pl-2 text-xs italic text-gray-500"
        >
          “{item.source_excerpt}”
          {(locator || item.observation_count) && (
            <span className="ml-1 not-italic text-[10px] text-gray-400">
              {[locator, item.observation_count && item.observation_count > 1
                ? `said ${item.observation_count}×`
                : null].filter(Boolean).join(' · ')}
            </span>
          )}
        </p>
      )}
    </li>
  );
};

export const DnaCard: React.FC<DnaCardProps> = ({ clientId, filters }) => {
  const { data: dna, isLoading, isError, refetch } = useClientDna(clientId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading DNA…
      </div>
    );
  }
  if (isError || !dna) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-red-600">
        <AlertCircle className="w-4 h-4" /> Could not load DNA.
        <button onClick={() => void refetch()} className="underline">
          Retry
        </button>
      </div>
    );
  }

  // Apply display filters: excluded sections empty out; items below the confidence
  // floor or unverified (when verifiedOnly) drop. Radar + counts read the same view.
  const filtersActive =
    !!filters &&
    (!!filters.sections?.length || filters.minConfidence !== undefined || !!filters.verifiedOnly);
  const itemVisible = (item: DnaItem) =>
    (filters?.minConfidence === undefined || (item.confidence ?? 0) >= filters.minConfidence) &&
    (!filters?.verifiedOnly || !!item.verified);
  const visibleDna: DnaResponse = { ...dna };
  for (const s of SECTIONS) {
    const sectionOn = !filters?.sections?.length || filters.sections.includes(s.key);
    visibleDna[s.key] = sectionOn ? (dna[s.key] ?? []).filter(itemVisible) : [];
  }

  const totalItems = SECTIONS.reduce((n, s) => n + (visibleDna[s.key]?.length ?? 0), 0);
  const hasContext = dna.temperament || dna.business_context || dna.family_context;

  if (totalItems === 0 && filtersActive) {
    return (
      <div className="p-4 text-sm text-gray-500">No DNA items match the active filters.</div>
    );
  }
  if (totalItems === 0 && !hasContext) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No DNA derived yet. Approving extracted facts in the KYC workspace derives it, or press
        “Derive DNA” there to propose values, exclusions and tilts from this client’s
        knowledge graph.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {totalItems > 0 && <DnaRadar dna={visibleDna} />}

      {dna.temperament && (
        <p className="text-sm italic text-gray-600 border-l-2 border-purple-200 pl-3">
          {dna.temperament}
        </p>
      )}

      {SECTIONS.map((section) => {
        const items = visibleDna[section.key] ?? [];
        if (items.length === 0) return null;
        return (
          <div key={section.key}>
            <h4 className={`inline-block px-2 py-0.5 mb-1 text-xs font-medium rounded border ${section.chip}`}>
              {section.label} ({items.length})
            </h4>
            <ul className="divide-y divide-gray-100">
              {items.map((item) => (
                <DnaItemRow key={item.entry_id} item={item} />
              ))}
            </ul>
          </div>
        );
      })}

      {(dna.business_context || dna.family_context) && (
        <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-100">
          {dna.business_context && <p><span className="font-medium">Business:</span> {dna.business_context}</p>}
          {dna.family_context && <p><span className="font-medium">Family:</span> {dna.family_context}</p>}
        </div>
      )}
    </div>
  );
};

export default DnaCard;
